import { Directive, Input, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import { LgvTSHelper } from '../utility/ts-helper';

/**
 * Configuration interface for mapping color parameters to CSS variables
 */
export interface ColorMapping {
  paramName: string;        // Name of the input parameter (e.g., 'primaryColor')
  cssVariables: string[];   // CSS variables to set (e.g., ['--primaryColor', '--buttonColor'])
  defaultValue?: string;    // Optional default value
  setRgbValues?: boolean;   // Whether to also set RGB values for transparency support
}

@Directive({
  selector: '[lusColorTheming]',
  standalone: true
})
export class ColorThemingDirective implements OnInit, OnDestroy, OnChanges {
  // Individual color inputs
  @Input() primaryColor: string;
  @Input() secondaryColor: string;
  @Input() accentColor: string;
  @Input() textColor: string;
  
  // Map of color parameters to CSS variables (with defaults)
  @Input() colorMappings: ColorMapping[] = [
    {
      paramName: 'primaryColor',
      cssVariables: ['--primaryColor'],
      setRgbValues: true
    }
  ];

  private styleElement: HTMLStyleElement | null = null;

  ngOnInit(): void {
    this.applyColors();
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.applyColors();
  }

  ngOnDestroy(): void {
    this.removeStyles();
  }

  private applyColors(): void {
    if (!this.styleElement) {
      this.styleElement = document.createElement('style');
    }
    
    let cssRules = '';
    
    // Process each color mapping
    this.colorMappings.forEach(mapping => {
      // Get the corresponding color value from inputs
      const colorValue = this[mapping.paramName];
      
      if (colorValue) {
        // Format the color if needed
        const formattedColor = this.formatColor(colorValue);
        
        // Add CSS rules for each variable in the mapping
        mapping.cssVariables.forEach(cssVar => {
          cssRules += `${cssVar}: ${formattedColor} !important;\n`;
        });
        
        // If this color needs RGB values for transparency support
        if (mapping.setRgbValues) {
          const rgbValues = LgvTSHelper.hexToRgbObject(formattedColor);
          if (rgbValues) {
            // Generate RGB CSS variable for each base variable by adding RGB suffix
            mapping.cssVariables.forEach(cssVar => {
              const rgbVar = cssVar + 'RGB';
              cssRules += `${rgbVar}: ${rgbValues.r}, ${rgbValues.g}, ${rgbValues.b} !important;\n`;
            });
          }
        }
      }
    });
    
    // Set the CSS rules
    this.styleElement.textContent = `
      :root {
        ${cssRules}
      }
    `;
    
    if (!this.styleElement.parentNode) {
      document.head.appendChild(this.styleElement);
    }
  }

  private formatColor(color: string): string {
    // Check if the color is a hex code without # prefix
    if (/^[0-9A-Fa-f]{3,6}$/.test(color)) {
      return '#' + color;
    }
    return color;
  }

  private removeStyles(): void {
    if (this.styleElement && this.styleElement.parentNode) {
      this.styleElement.parentNode.removeChild(this.styleElement);
      this.styleElement = null;
    }
  }
} 